import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Sparkles, ListChecks, Mail, Mic, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import BloqLogo from "@/components/BloqLogo";

/**
 * GuestThanks — shown to non-logged-in attendees AFTER they leave a meeting
 * or after the host ends the meeting for everyone. Purpose:
 *   1. Acknowledge they were just in the meeting (warm tone).
 *   2. Invite them to create a Bloq account so the next meeting's recap,
 *      action items, and follow-up emails are saved for them too.
 *
 * Per Iter 20 spec: no "Rejoin lobby" button — clicking it caused a React
 * concurrent rendering error (the destination Lobby would immediately
 * detect the ended status and trigger another navigate during render).
 * Logged-in users are routed to /app (the dashboard) instead — they never
 * land here.
 */
export default function GuestThanks() {
  const navigate = useNavigate();
  // Defensive: if a logged-in user somehow lands here, send them home.
  useEffect(() => {
    try {
      const t = localStorage.getItem("bloq_token");
      if (t) navigate("/app", { replace: true });
    } catch {}
  }, [navigate]);
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background:
          "radial-gradient(1100px 700px at 100% 0%, rgba(0,94,122,0.25), transparent 60%)," +
          "radial-gradient(900px 600px at 0% 100%, rgba(255,206,113,0.25), transparent 60%)," +
          "#f5efe6",
      }}
      data-testid="guest-thanks-page"
    >
      <header className="px-6 py-5">
        <BloqLogo size={26} />
      </header>
      <main className="flex-1 grid place-items-center px-6 py-12">
        <div
          className="max-w-2xl w-full bg-white shadow-xl border border-[#e4e0d8] p-10"
          style={{ borderRadius: 0 }}
        >
          <p className="bloq-eyebrow text-[#005e7a]">Your meeting just wrapped</p>
          <h1 className="bloq-headline text-4xl sm:text-5xl mt-3 leading-tight">
            Want the recap magic next time?
          </h1>
          <p className="text-base text-[#4a4a52] mt-4 max-w-xl">
            Create a Bloq account to keep meeting notes, action items, summaries,
            and follow-ups in one place — automatically.
          </p>
          <ul className="grid sm:grid-cols-2 gap-3 mt-7">
            <Feature icon={Sparkles} title="AI summaries" desc="Every meeting auto-summarized." />
            <Feature icon={ListChecks} title="Action items" desc="Captured and assignable." />
            <Feature icon={Mail} title="Follow-up emails" desc="Drafted by AI, sent in one click." />
            <Feature icon={Mic} title="Recall reports" desc="DOCX & PDF exports of every call." />
          </ul>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link
              to="/beta-disclaimer"
              className="bloq-btn-primary inline-flex items-center gap-2"
              data-testid="guest-thanks-create-account"
              style={{ borderRadius: 0 }}
            >
              Create account <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/login"
              className="bloq-btn-secondary"
              data-testid="guest-thanks-login"
              style={{ borderRadius: 0 }}
            >
              I have an account
            </Link>
          </div>
        </div>
      </main>
      <footer className="px-6 py-5 text-xs text-[#82828c]">
        Powered by Bloq · bloq.video
      </footer>
    </div>
  );
}

function Feature({ icon: Icon, title, desc }) {
  return (
    <li className="flex items-start gap-3 p-3 bg-[#faf7f2] border border-[#e4e0d8]" style={{ borderRadius: 0 }}>
      <div
        className="w-8 h-8 flex items-center justify-center bg-[#005e7a]/10 text-[#005e7a] flex-shrink-0"
        style={{ borderRadius: 0 }}
      >
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-[#82828c]">{desc}</p>
      </div>
    </li>
  );
}
